import React from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import FormatDateTime from 'components/common/FormatDateTime';
import type { SubmitScheduleModel } from './SubmitScheduleModel';

interface SchedulePreviewPanelProps {
  readonly schedule: SubmitScheduleModel;
  /** moment token for the time part, per the 12h/24h preference. */
  readonly displayTimeToken: string;
}

/**
 * What the submit will actually do.
 *
 * Exists because two things are invisible on the form itself: a closed
 * scheduling window silently pushes the start later, and a recurrence's
 * start_time applies only from the second occurrence — so the gap between the
 * first two runs can differ from the rule's interval. Both are surfaced here
 * rather than corrected behind the operator's back.
 *
 * Nothing here is live. The answer is anchored to the instant it was calculated,
 * so that instant is stated and refreshing is the operator's deliberate act —
 * see {@link SubmitScheduleModel.previewAsOf}.
 */
const SchedulePreviewPanel: React.FC<SchedulePreviewPanelProps> = observer(({ schedule, displayTimeToken }) => {
  const { preview } = schedule;
  const timezone = schedule.effectiveTimezone;

  /**
   * Renders a schedule datetime — a wall-clock string meaningful in the form's
   * selected timezone, NOT an instant.
   *
   * Not `<FormatDateTime>`, for two reasons. It takes an absolute instant and
   * would need a conversion here first, since `new Date('2026-08-06T00:01:00')`
   * reads an offset-free string as browser-local and `sourceTimezone` only
   * chooses the rendering zone — the two together would shift the value twice.
   * And it cannot emit a zone abbreviation, which is load-bearing here: these
   * are times the operator is about to commit to in a zone they chose, so
   * "00:01 CEST" and a bare "00:01" are not the same claim.
   */
  const format = (value: string): string => {
    const parsed = moment.tz(value, timezone);
    return parsed.isValid() ? parsed.format(`YYYY-MM-DD ${displayTimeToken} z`) : value;
  };

  const pending = <Spinner animation="border" size="sm" role="status" aria-label="Checking the schedule" />;

  // `previewAsOf` is an absolute instant, which is what FormatDateTime wants —
  // so it also picks up the local-timezone preference, and rendering it in the
  // form's zone keeps it comparable with the schedule times above it.
  const asOfLine = schedule.previewAsOf && (
    <div className="text-muted small d-flex align-items-center gap-2 mt-2">
      <span>
        Checked at <FormatDateTime value={schedule.previewAsOf} sourceTimezone={timezone} />
      </span>
      <Button variant="link" size="sm" className="p-0 lh-1 text-decoration-none" onClick={() => void schedule.refreshNow()} disabled={schedule.isPreviewStale}>
        <FontAwesomeIcon icon={faArrowsRotate} className="fs-10 me-1" />
        Refresh
      </Button>
    </div>
  );

  // A failed result must not be left on screen while the next attempt is in
  // flight. The slow case is exactly the failing one — a window with no opening
  // makes the backend rescan two years before giving up — so a stale error with
  // no pending indicator is precisely when this reads as a hang.
  if (schedule.isPreviewStale && preview.kind !== 'ready') {
    return (
      <div className="text-muted small d-flex align-items-center gap-2">
        {pending}
        Checking the schedule…
      </div>
    );
  }

  if (preview.kind === 'unschedulable') {
    return (
      <Alert variant="outline-danger" className="mb-0">
        <div className="fw-semibold">Cannot be scheduled</div>
        <div className="small">{preview.message}</div>
        {asOfLine}
      </Alert>
    );
  }

  if (preview.kind === 'error') {
    return (
      <Alert variant="outline-warning" className="mb-0">
        <div className="fw-semibold">Could not determine the start</div>
        <div className="small">{preview.message}</div>
        {asOfLine}
      </Alert>
    );
  }

  return (
    <div>
      <div className="text-muted small d-flex align-items-center gap-2">
        Starts
        {/* A known-outdated value is greyed with the spinner rather than hidden,
            so the panel does not flicker on every keystroke. */}
        {schedule.isPreviewStale && pending}
      </div>
      {schedule.startsOnSubmit ? (
        <div className="fw-semibold">Immediately, when you submit</div>
      ) : (
        <div className={`fw-semibold ${schedule.isPreviewStale ? 'text-muted' : ''}`}>{format(schedule.actualStart)}</div>
      )}

      {schedule.movedByWindow && (
        <div className="text-muted small mt-1">
          Moved from {format(schedule.finalScheduled)} — scheduling window <code>{schedule.movedByWindow}</code> was closed.
        </div>
      )}

      {preview.occurrences.length > 0 && (
        <>
          <div className="text-muted small mt-3">Then</div>
          <ul className="list-unstyled mb-0 small">
            {preview.occurrences.map((occurrence) => (
              <li key={occurrence}>{format(occurrence)}</li>
            ))}
          </ul>
        </>
      )}

      {asOfLine}
    </div>
  );
});

export default SchedulePreviewPanel;
